import {
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  IconArrowLeft,
  IconArrowRight,
  IconCheck,
  IconPencil,
  IconSearch,
  IconSparkles,
  IconWorldSearch,
} from '@tabler/icons-react';
import { actionButtonSpacing, gridSpacing } from 'constants/themeConstants';
import usePartsV2Search from 'hooks/usePartsV2Search';
import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import SegmentButton from 'ui-component/Segment/SegmentButton';
import { PartTypeFilterIds } from 'views/parts/PartsV2/Parts/constants';
import CreatePartForm from 'ui-component/CreateNewOrgPartDialog/components/CreatePartForm';
import {
  OrgPartSchemaLite,
  OrgPartScrapeResponseSchema,
  OrgPartType,
  PublicPartSchema,
  PublicPartSchemaLite,
  PublicPartSchemaSearch,
} from 'types/clientV2/parts';
import useSnackbar from 'hooks/useSnackbar';
import { RequestPersistenceType, useAsyncRequest } from 'hooks/useAsyncRequest';
import { useScrapeOrgPartMutation } from 'store/slices/clientV2/parts';
import {
  OrgPartFormConstants,
  OrgPartFormInputData,
} from 'ui-component/CreateNewOrgPartDialog/constants';
import _ from 'lodash';
import { OrgSupplier } from 'types/suppliers';
import {
  useGetOrgSuppliersServerSideQuery,
  useMatchOrCreateOrgSupplierMutation,
} from 'store/slices/apiV1/suppliers';
import { DISABLE_PAGE_LIMIT, QueryFilterOperators } from 'types/api';
import {
  UNLIMITED_QUANTITY,
  UNLIMITED_QUANTITY_STRING,
} from 'constants/procurementConstants';
import {
  UnitOfMeasureDetails,
  UnitOfMeasureOptions,
} from 'types/unitsOfMeasure';
import { formatUnlimitedQuant, handleErr } from 'utils/functions';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useTheme } from '@mui/material/styles';
import PartV2Cell from 'ui-component/DataGrid/Cell/PartV2Cell';
import { RenderPartV2CellProps } from 'ui-component/DataGrid/Render/RenderPartV2Cell';
import WithSkeleton from 'ui-component/extended/WithSkeleton';
import { PartSchemas, partStatusRequestTypes } from 'types/partStatus';
import { lifeCycleStatusFormats } from 'types/part';
import InlineGoToButton from 'ui-component/shared/InlineGoToButton';
import { ALL_APPS } from 'constants/appConstants';
import Chip from 'ui-component/extended/Chip';
import { useGetOrgQuery } from 'store/slices/apiV1/org';
import { useSelector } from 'store';
import { HookFormInput } from 'ui-component/HookFormComponents';
import { useLazyGetSerpSearchQuery } from 'store/slices/apiV1/utility';
import { SerpSearchResult } from 'types/utility';
import { stripUrlToDomain, stripUrlToDomainWithTld } from 'utils/urls';
import useServerSideQuery from 'hooks/useServerSideQuery';
import { MatchOrgSupplierSubSchema } from 'ui-component/CreateNewOrgPartDialog/components/PartDetailsForm';
import SerpResultRow from 'ui-component/CreateNewOrgPartDialog/components/SerpResultRow';

type OtsPartWizardProps = {
  setHeroImage: (file: File | null) => void;
  defaultValues: OrgPartFormInputData;
  searchPrefill?: string;
  skipSearch?: boolean;
  scrapePageContent?: string;
  orgSupplierId?: OrgSupplier['id'];
};

enum OtsPartWizardStep {
  SEARCH = 'search',
  SCRAPE_PART = 'scrape_part',
  EDIT_PART = 'edit_part',
  ADD_PUBLIC_PART = 'add_public_part',
}

const OtsPartWizard = ({
  setHeroImage,
  defaultValues,
  searchPrefill,
  skipSearch,
  scrapePageContent,
  orgSupplierId,
}: OtsPartWizardProps) => {
  const theme = useTheme();
  const { activeOrgId } = useSelector((state) => state.org);
  const { data: org } = useGetOrgQuery(activeOrgId ?? skipToken);
  const { dispatchErrorSnackbar } = useSnackbar();
  const [step, setStep] = useState<OtsPartWizardStep>(OtsPartWizardStep.SEARCH);
  const [serpResults, setSerpResults] = useState<SerpSearchResult[]>([]);
  const [scrapeUrl, setScrapeUrl] = useState<string | null>(null);
  const lastProcessedUrlRef = useRef<string | null>(null);

  const {
    bothParts,
    searchString,
    debouncedUrlSearch,
    handleSetSearchString,
    handleImmediateSearch,
    supply: { supplyStatus },
  } = usePartsV2Search({
    defaultSelectedPartType: PartTypeFilterIds.BOTH,
  });

  const [scrapeOrgPart, { jobMessage }] = useAsyncRequest(
    useScrapeOrgPartMutation,
    RequestPersistenceType.component
  );
  const scrapeOrgPartRef = useRef(scrapeOrgPart);
  useEffect(() => {
    scrapeOrgPartRef.current = scrapeOrgPart;
  }, [scrapeOrgPart]);

  const [getSerpSearch, { isLoading: isSerpSearchLoading }] =
    useLazyGetSerpSearchQuery();

  const {
    setValue,
    getValues,
    reset,
    control,
    formState: { errors },
  } = useFormContext();

  const publicPartId = useWatch({
    control,
    name: OrgPartFormConstants.publicPart.id,
  });

  const matchedPublicPart = useMemo(
    () => bothParts.parts.find((row) => row.part.id === publicPartId),
    [bothParts.parts, publicPartId]
  );

  const matchedPublicPartLifecycleFormat = useMemo(
    () =>
      lifeCycleStatusFormats[matchedPublicPart?.part?.lifecycleStatus ?? ''],
    [matchedPublicPart?.part?.lifecycleStatus]
  );

  const handleSerpSearch = useCallback(
    async (searchQuery: string) => {
      try {
        const results = await getSerpSearch({ query: searchQuery }).unwrap();
        setSerpResults(results.results);
      } catch (err) {
        handleErr(err, (errMessage: string) => {
          dispatchErrorSnackbar(errMessage);
        });
      }
    },
    [getSerpSearch, dispatchErrorSnackbar]
  );

  const handleSearch = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | undefined
  ) => {
    const newString = event?.target.value;
    handleSetSearchString(newString || '');
    setSerpResults([]); // reset serp results when search string changes
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (searchString.trim().startsWith('http')) {
        setStep(OtsPartWizardStep.SCRAPE_PART);
      } else {
        handleImmediateSearch(searchString.trim());
      }
    }
  };

  const { data: matchOrgSuppliers } = useServerSideQuery<
    OrgSupplier,
    MatchOrgSupplierSubSchema
  >(useGetOrgSuppliersServerSideQuery, {
    pageSize: DISABLE_PAGE_LIMIT,
    schema: [
      'id',
      'name',
      'manufacturerMatchRegex',
      'mpnMatchRegex',
      'autoScrape',
      'productPatchUrl',
    ],
    filters: [
      {
        field: 'manufacturerMatchRegex',
        operator: QueryFilterOperators.isNotEmpty,
        value: true,
      },
      {
        field: 'mpnMatchRegex',
        operator: QueryFilterOperators.isNotEmpty,
        value: true,
      },
    ],
  });

  const matchingOrgSuppliers = useMemo(() => {
    if (debouncedUrlSearch) return [];
    // if search string contains spaces, split and check if any of the parts match the mpn match regex and any of the parts match the manufacturer match regex
    const parts = searchString.split(' ');
    let matchingSuppliers = _.chain(matchOrgSuppliers?.data || [])
      .map((supplier) => {
        if (
          !supplier.manufacturerMatchRegex ||
          !supplier.mpnMatchRegex ||
          !supplier.autoScrape ||
          !supplier.productPatchUrl
        )
          return false;
        try {
          const mfgRegex = new RegExp(supplier.manufacturerMatchRegex, 'i');
          const mpnRegex = new RegExp(supplier.mpnMatchRegex, 'i');
          const matchesMfg = _.some(parts, (part) => mfgRegex.test(part));
          const matchesMpn = _.some(parts, (part) => mpnRegex.test(part));
          const mpnPart = _.find(parts, (part) => mpnRegex.test(part));
          if (matchesMfg && matchesMpn && mpnPart) {
            return {
              ...supplier,
              productUrl: supplier.productPatchUrl.replace('{{mpn}}', mpnPart),
              mpn: mpnPart,
            };
          }
        } catch {
          return false;
        }
        return false;
      })
      .compact()
      .value();

    if (matchingSuppliers.length > 0) return matchingSuppliers;

    matchingSuppliers = _.chain(matchOrgSuppliers?.data || [])
      .map((supplier) => {
        if (
          !supplier.manufacturerMatchRegex ||
          !supplier.mpnMatchRegex ||
          !supplier.autoScrape ||
          !supplier.productPatchUrl
        )
          return false;
        try {
          const mpnRegex = new RegExp(supplier.mpnMatchRegex, 'i');
          const matchesMpn = _.some(parts, (part) => mpnRegex.test(part));
          const mpnPart = _.find(parts, (part) => mpnRegex.test(part));
          if (matchesMpn && mpnPart) {
            return {
              ...supplier,
              productUrl: supplier.productPatchUrl.replace('{{mpn}}', mpnPart),
              mpn: mpnPart,
            };
          }
        } catch {
          return false;
        }
        return false;
      })
      .compact()
      .value();

    return matchingSuppliers;
  }, [matchOrgSuppliers, searchString, debouncedUrlSearch]);

  const getIsSupplyLoading = useCallback(
    (cofactrId: string | null) => {
      if (!cofactrId) {
        return false;
      }

      return (
        _.get(supplyStatus[cofactrId], PartSchemas.supply) !==
        partStatusRequestTypes.SUCCESS
      );
    },
    [supplyStatus]
  );

  const searchStringIsLink = useMemo(
    () => searchString.trim().startsWith('http'),
    [searchString]
  );

  const strippedUrlWithTld = useMemo(
    () => stripUrlToDomainWithTld(scrapeUrl),
    [scrapeUrl]
  );

  const [matchOrCreateOrgSupplier] = useMatchOrCreateOrgSupplierMutation();

  const handleSetPublicPart = (publicPart: PublicPartSchemaSearch) => {
    const formUpdates = {
      [OrgPartFormConstants.linkMatchedPart.id]: true,
      [OrgPartFormConstants.publicPart.id]: publicPart.id,
      [OrgPartFormConstants.mfg.id]: publicPart.manufacturer,
      [OrgPartFormConstants.mpn.id]: publicPart.mpn,
      [OrgPartFormConstants.msl.id]: publicPart.msl,
      [OrgPartFormConstants.packageType.id]: publicPart.package,
      [OrgPartFormConstants.description.id]: publicPart.description,
      [OrgPartFormConstants.classification.id]: publicPart.primaryClass,
      [OrgPartFormConstants.mountType.id]: publicPart.terminationType,
    };

    _.chain(formUpdates)
      .entries()
      .forEach(([field, value]) => {
        setValue(field, value);
      })
      .value();

    setStep(OtsPartWizardStep.ADD_PUBLIC_PART);
  };

  const handleScrapePartRef = useRef<((url: string) => Promise<void>) | null>(
    null
  );

  const handleScrapePart = useCallback(
    async (url: string) => {
      if (url) {
        try {
          setValue(OrgPartFormConstants.link.id, url);
          const response = (await scrapeOrgPartRef.current({
            url,
            includePartData: true,
            includeSupplierQuoteData: true,
            pageContent: scrapePageContent,
          })) as OrgPartScrapeResponseSchema;

          const orgSupplierResult = orgSupplierId
            ? { id: orgSupplierId }
            : await matchOrCreateOrgSupplier({
                orgNameOrDomain: stripUrlToDomain(url) ?? '',
              }).unwrap();

          const formUpdates = {
            [OrgPartFormConstants.linkMatchedPart.id]: false,
            [OrgPartFormConstants.scrapedFields.id]: [
              ..._.chain(response.partData)
                .pickBy((value) => !!value)
                .keys()
                .map((k) =>
                  _.get(
                    {
                      manufacturer: OrgPartFormConstants.mfg.id,
                      mpn: OrgPartFormConstants.mpn.id,
                      description: OrgPartFormConstants.description.id,
                      specs: OrgPartFormConstants.specs.id,
                      isElectronicComponent:
                        OrgPartFormConstants.isElectronicComponent.id,
                      terminationType: OrgPartFormConstants.mountType.id,
                      package: OrgPartFormConstants.packageType.id,
                      msl: OrgPartFormConstants.msl.id,
                      heroImageUrl: OrgPartFormConstants.heroImageUrl.id,
                      dimensionClass: OrgPartFormConstants.dimensionClass.id,
                      preferredUnit: OrgPartFormConstants.preferredUnit.id,
                    },
                    k,
                    null
                  )
                )
                .compact()
                .value(),
              ..._.chain(response.supplierQuoteData)
                .pickBy((value) => !!value)
                .keys()
                .map((k) =>
                  _.get(
                    {
                      shipsInLead: OrgPartFormConstants.shipsInLead.id,
                      sku: OrgPartFormConstants.sku.id,
                      packaging: OrgPartFormConstants.packaging.id,
                      coa: OrgPartFormConstants.coa.id,
                      moq: OrgPartFormConstants.moq.id,
                      multiple: OrgPartFormConstants.multiple.id,
                      maximum: OrgPartFormConstants.maximum.id,
                      prices: OrgPartFormConstants.prices.id,
                      dimensionUnit: OrgPartFormConstants.dimensionUnit.id,
                      dimensionClass: OrgPartFormConstants.dimensionClass.id,
                      dimensionValue: OrgPartFormConstants.dimensionValue.id,
                    },
                    k,
                    null
                  )
                )
                .compact()
                .value(),
            ],
            [OrgPartFormConstants.stockDocument.id]: response.stockDocument,
            [OrgPartFormConstants.mfg.id]: response.partData.manufacturer,
            [OrgPartFormConstants.mpn.id]: response.partData.mpn,
            [OrgPartFormConstants.preferredUnit.id]:
              response.partData.preferredUnit,
            [OrgPartFormConstants.description.id]:
              response.partData.description,
            [OrgPartFormConstants.specs.id]: _.chain(response.partData.specs)
              .filter((spec) => !!spec.value)
              .keyBy('label')
              .mapValues('value')
              .value(),
            [OrgPartFormConstants.isElectronicComponent.id]:
              response.partData.isElectronicComponent,
            [OrgPartFormConstants.mountType.id]:
              response.partData.terminationType,
            [OrgPartFormConstants.packageType.id]: response.partData.package,
            [OrgPartFormConstants.msl.id]: response.partData.msl,
            [OrgPartFormConstants.heroImageUrl.id]:
              response.partData.heroImageUrl,
            [OrgPartFormConstants.supplierQuoteOpen.id]:
              !!response.supplierQuoteData.prices,
            [OrgPartFormConstants.shipsInLead.id]:
              response.supplierQuoteData.shipsInLead,
            [OrgPartFormConstants.sku.id]: response.supplierQuoteData.sku,
            [OrgPartFormConstants.packaging.id]:
              response.supplierQuoteData.packaging,
            [OrgPartFormConstants.coa.id]: response.supplierQuoteData.coa,
            [OrgPartFormConstants.moq.id]:
              _.toNumber(response.supplierQuoteData.moq) || 1,
            [OrgPartFormConstants.multiple.id]:
              _.toNumber(response.supplierQuoteData.multiple) ||
              _.toNumber(response.supplierQuoteData.packageQuantity) ||
              1,
            [OrgPartFormConstants.maximum.id]:
              _.toNumber(response.supplierQuoteData.maximum) ===
              UNLIMITED_QUANTITY
                ? UNLIMITED_QUANTITY_STRING
                : _.toNumber(response.supplierQuoteData.maximum) ||
                  UNLIMITED_QUANTITY_STRING,
            [OrgPartFormConstants.dimensionUnit.id]:
              response.supplierQuoteData.dimensionUnit ||
              response.partData.preferredUnit ||
              UnitOfMeasureOptions.PIECE,
            [OrgPartFormConstants.dimensionClass.id]:
              UnitOfMeasureDetails[
                response.supplierQuoteData.dimensionUnit ||
                  response.partData.preferredUnit ||
                  UnitOfMeasureOptions.PIECE
              ].dimensionClass,
            [OrgPartFormConstants.dimensionValue.id]:
              response.supplierQuoteData.packageQuantity || 1,
            [OrgPartFormConstants.prices.id]: _.map(
              response.supplierQuoteData.prices || [],
              (price) => ({
                to: price.from,
                unitPrice: price.unitPrice,
              })
            ),
          };

          reset(
            {
              ...getValues(),
              ...formUpdates,
            },
            {
              keepDefaultValues: true,
              keepDirty: true,
              keepErrors: true,
              keepIsSubmitted: true,
              keepTouched: true,
            }
          );

          setValue(
            OrgPartFormConstants.orgSupplier.id,
            orgSupplierResult?.id ?? null
          );

          setStep(OtsPartWizardStep.EDIT_PART);
        } catch (err) {
          handleErr(err, (errMessage: string) => {
            dispatchErrorSnackbar(errMessage);
          });
        }
      }
    },
    [
      setValue,
      matchOrCreateOrgSupplier,
      reset,
      getValues,
      setStep,
      dispatchErrorSnackbar,
      scrapePageContent,
      orgSupplierId,
    ]
  );

  useEffect(() => {
    handleScrapePartRef.current = handleScrapePart;
  }, [handleScrapePart]);

  useEffect(() => {
    if (debouncedUrlSearch) {
      if (lastProcessedUrlRef.current !== debouncedUrlSearch) {
        lastProcessedUrlRef.current = debouncedUrlSearch;
        setScrapeUrl(debouncedUrlSearch);
        setStep(OtsPartWizardStep.SCRAPE_PART);
        handleScrapePartRef.current?.(debouncedUrlSearch);
      }
    } else {
      lastProcessedUrlRef.current = null;
      setStep(OtsPartWizardStep.SEARCH);
    }
  }, [debouncedUrlSearch]);

  useEffect(() => {
    setSerpResults([]);
    if (searchPrefill && !skipSearch) {
      handleSetSearchString(searchPrefill);
    }
    if (skipSearch) {
      setStep(OtsPartWizardStep.EDIT_PART);
    }
  }, [searchPrefill, skipSearch, handleSetSearchString]);

  useEffect(() => {
    if (
      searchString.length > 5 &&
      bothParts.parts.length === 0 &&
      !bothParts.isLoading
    ) {
      handleSerpSearch(searchString.trim());
    }
  }, [
    searchString,
    bothParts.isLoading,
    bothParts.parts.length,
    handleSerpSearch,
  ]);

  return (
    <Stack
      direction="column"
      spacing={gridSpacing}
      sx={{
        justifyContent: 'flex-start',
        alignItems: 'stretch',
      }}
    >
      {/* Search Bar */}
      <Grid
        container
        spacing={actionButtonSpacing}
        sx={{
          alignItems: 'center',
        }}
      >
        {(step !== OtsPartWizardStep.EDIT_PART || searchString.length > 0) && (
          <Grid size="grow">
            <TextField
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {searchStringIsLink ? (
                      <IconWorldSearch stroke={1.5} fontSize="small" />
                    ) : (
                      <IconSearch stroke={1.5} fontSize="small" />
                    )}
                  </InputAdornment>
                ),
                endAdornment:
                  (bothParts.isLoading || isSerpSearchLoading) &&
                  step === OtsPartWizardStep.SEARCH ? (
                    <CircularProgress size={20} />
                  ) : (
                    <InputAdornment
                      position="start"
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleImmediateSearch(searchString.trim())}
                    >
                      <IconArrowRight stroke={1.5} fontSize="small" />
                    </InputAdornment>
                  ),
              }}
              onChange={handleSearch}
              onKeyDown={handleKeyDown}
              placeholder="Search for a part number or manufacturer, or paste a link to a part"
              value={searchString}
              size="small"
              disabled={
                step === OtsPartWizardStep.EDIT_PART ||
                step === OtsPartWizardStep.SCRAPE_PART ||
                step === OtsPartWizardStep.ADD_PUBLIC_PART
              }
            />
          </Grid>
        )}
        <Grid size="auto">
          {step !== OtsPartWizardStep.EDIT_PART &&
          step !== OtsPartWizardStep.ADD_PUBLIC_PART ? (
            <SegmentButton
              variant="outlined"
              color="primary"
              onClick={() => setStep(OtsPartWizardStep.EDIT_PART)}
              startIcon={<IconPencil />}
            >
              Enter Info Manually
            </SegmentButton>
          ) : (
            <SegmentButton
              variant="outlined"
              color="primary"
              onClick={() => {
                reset(defaultValues);
                setStep(OtsPartWizardStep.SEARCH);
              }}
              startIcon={<IconArrowLeft />}
            >
              Back
            </SegmentButton>
          )}
        </Grid>
      </Grid>
      {/* Matching Org Suppliers */}
      {matchingOrgSuppliers.length > 0 && step === OtsPartWizardStep.SEARCH && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          {matchingOrgSuppliers.map((supplier) => (
            <SegmentButton
              key={supplier.id}
              color="primary"
              onClick={() => {
                setScrapeUrl(supplier.productUrl);
                setStep(OtsPartWizardStep.SCRAPE_PART);
                handleScrapePart(supplier.productUrl);
              }}
              startIcon={
                <IconSparkles
                  width={20}
                  height={20}
                  color={theme.palette.success.main}
                />
              }
            >
              {supplier.name} {supplier.mpn || ''}
            </SegmentButton>
          ))}
        </Stack>
      )}
      {/* Scrape Loading State */}
      {step === OtsPartWizardStep.SCRAPE_PART && (
        <Grid container alignItems="center" justifyContent="center">
          <Grid size="auto" sx={{ marginTop: '20px' }}>
            <CircularProgress
              size={90}
              thickness={2.5}
              sx={{ color: theme.palette.primary[600] }}
            />
          </Grid>
          <Grid size={12} sx={{ marginTop: '20px' }}>
            <Typography variant="h4" sx={{ textAlign: 'center' }}>
              Ailana is loading and analyzing the selected page from{' '}
              {strippedUrlWithTld}
            </Typography>
            {!!jobMessage && (
              <Typography variant="h4" sx={{ textAlign: 'center' }}>
                {jobMessage}
              </Typography>
            )}
          </Grid>
        </Grid>
      )}
      {/* Component Cloud Search Results */}
      {bothParts.parts.length > 0 && step === OtsPartWizardStep.SEARCH && (
        <Grid container>
          {bothParts.parts.map((row) => {
            const { part, partDataType } = row;
            const isPublicPartDataType =
              partDataType === PartTypeFilterIds.PUBLIC;
            const props = {
              orgPart: isPublicPartDataType
                ? undefined
                : (part as OrgPartSchemaLite),
              publicPart: isPublicPartDataType
                ? (part as PublicPartSchemaLite)
                : undefined,
              searchParams: searchString,
            } as RenderPartV2CellProps;
            const lifeCycle = _.get(part, 'lifecycleStatus', undefined);
            const lifecycleFormat = _.get(
              lifeCycleStatusFormats,
              lifeCycle || ''
            );

            const suppliers = _.chain(matchOrgSuppliers?.data || [])
              .map((supplier) => {
                if (
                  !supplier.manufacturerMatchRegex ||
                  !supplier.mpnMatchRegex ||
                  !supplier.autoScrape ||
                  !supplier.productPatchUrl
                )
                  return false;
                try {
                  const mfgRegex = new RegExp(
                    supplier.manufacturerMatchRegex,
                    'i'
                  );
                  const mpnRegex = new RegExp(supplier.mpnMatchRegex, 'i');
                  const matchesMfg = mfgRegex.test(part.manufacturer ?? '');
                  const matchesMpn = mpnRegex.test(part.mpn ?? '');
                  if (matchesMfg && matchesMpn) {
                    return {
                      ...supplier,
                      productUrl: supplier.productPatchUrl.replace(
                        '{{mpn}}',
                        part.mpn ?? ''
                      ),
                    };
                  }
                } catch {
                  return false;
                }
                return false;
              })
              .compact()
              .value();

            return (
              <Grid
                key={row.id}
                container
                size={12}
                justifyContent="space-between"
                alignItems="center"
                direction="row"
                onClick={
                  isPublicPartDataType
                    ? () => {
                        handleSetPublicPart(row.part as PublicPartSchemaSearch);
                      }
                    : () => {}
                }
                sx={{
                  padding: theme.spacing(1, 2),
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  '&:hover': isPublicPartDataType
                    ? {
                        backgroundColor: theme.palette.action.hover,
                      }
                    : {},
                  '&:last-child': {
                    borderBottom: 'none',
                  },
                }}
              >
                <Grid container size={4} justifyContent="flex-start">
                  <PartV2Cell {...props} />
                </Grid>
                <Grid container size={4} justifyContent="center">
                  <Stack
                    direction="row"
                    spacing={3}
                    justifyContent="center"
                    alignItems="center"
                  >
                    {['buyable', 'quotable', 'maybe'].map((stockType) => {
                      const supplyData = _.get(
                        row?.supply,
                        stockType,
                        undefined
                      ) as number | undefined;
                      const isSupplyLoading = getIsSupplyLoading(
                        row?.part?.cofactrId
                      );
                      return (
                        <Stack
                          key={stockType}
                          direction="column"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <WithSkeleton isLoading={isSupplyLoading}>
                            <Typography>
                              {formatUnlimitedQuant(supplyData ?? 0)}
                            </Typography>
                          </WithSkeleton>
                          <Typography variant="subtitle2">
                            {stockType}
                          </Typography>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Grid>
                <Grid container size={2} justifyContent="flex-start">
                  {lifecycleFormat && (
                    <Tooltip title={lifecycleFormat.tooltip}>
                      <Typography color={lifecycleFormat.color}>
                        {lifecycleFormat.label}
                      </Typography>
                    </Tooltip>
                  )}
                </Grid>
                <Grid container size={2} justifyContent="flex-end">
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                    }}
                  >
                    {row.source === PartTypeFilterIds.ORG ||
                    row.source === PartTypeFilterIds.BOTH ? (
                      <Chip label="Already in Library" color="primary" />
                    ) : suppliers.length > 0 ? (
                      <SegmentButton
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          setScrapeUrl(suppliers[0].productUrl);
                          setStep(OtsPartWizardStep.SCRAPE_PART);
                          handleScrapePart(suppliers[0].productUrl);
                        }}
                        startIcon={<IconSparkles />}
                      >
                        Select
                      </SegmentButton>
                    ) : (
                      <SegmentButton
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          handleSetPublicPart(
                            row.part as PublicPartSchemaSearch
                          );
                        }}
                        startIcon={<IconCheck />}
                      >
                        Select
                      </SegmentButton>
                    )}
                    <InlineGoToButton
                      link={`${ALL_APPS.PARTS.path}/${row.id}`}
                      tooltip="View Part"
                      recordName="Part"
                    />
                  </Stack>
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      )}
      {/* Search Web Button */}
      {step === OtsPartWizardStep.SEARCH &&
        bothParts.parts.length > 0 &&
        serpResults.length === 0 &&
        !isSerpSearchLoading && (
          <Divider flexItem>
            <SegmentButton
              startIcon={<IconSearch />}
              color="primary"
              onClick={() => handleSerpSearch(searchString.trim())}
            >
              Search the web for more results
            </SegmentButton>
          </Divider>
        )}
      {/* Component Cloud & Serp Divider */}
      {serpResults.length > 0 && bothParts.parts.length > 0 && (
        <Divider flexItem />
      )}
      {/* Serp Search Results */}
      {serpResults.length > 0 && step === OtsPartWizardStep.SEARCH && (
        <Grid container>
          {serpResults.map((result) => (
            <SerpResultRow
              key={result.link}
              result={result}
              onClick={() => {
                setScrapeUrl(result.link);
                setStep(OtsPartWizardStep.SCRAPE_PART);
                handleScrapePart(result.link);
              }}
            />
          ))}
        </Grid>
      )}
      {/* No Search Results State */}
      {step === OtsPartWizardStep.SEARCH &&
        serpResults.length === 0 &&
        !isSerpSearchLoading &&
        searchString.length > 0 &&
        !bothParts.isLoading &&
        bothParts.parts.length === 0 && (
          <Typography variant="body1">No search results found</Typography>
        )}
      {/* Edit Part Form */}
      {step === OtsPartWizardStep.EDIT_PART && (
        <CreatePartForm
          formRenderMode={OrgPartType.OTS}
          setHeroImage={setHeroImage}
          hideLink
        />
      )}
      {/* Add Public Part Form */}
      {step === OtsPartWizardStep.ADD_PUBLIC_PART &&
        matchedPublicPart?.part && (
          <Stack
            direction="column"
            spacing={2}
            sx={{
              justifyContent: 'flex-start',
              alignItems: 'stretch',
            }}
          >
            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              direction="row"
              sx={{
                padding: theme.spacing(1, 2),
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Grid container size={4} justifyContent="flex-start">
                <PartV2Cell
                  publicPart={matchedPublicPart?.part as PublicPartSchema}
                />
              </Grid>
              <Grid container size={4} justifyContent="center">
                <Stack
                  direction="row"
                  spacing={3}
                  justifyContent="center"
                  alignItems="center"
                >
                  {['buyable', 'quotable', 'maybe'].map((stockType) => {
                    const supplyData = _.get(
                      matchedPublicPart?.supply,
                      stockType,
                      undefined
                    ) as number | undefined;
                    const isSupplyLoading = getIsSupplyLoading(
                      matchedPublicPart?.part?.cofactrId ?? null
                    );
                    return (
                      <Stack
                        key={stockType}
                        direction="column"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <WithSkeleton isLoading={isSupplyLoading}>
                          <Typography>
                            {formatUnlimitedQuant(supplyData ?? 0)}
                          </Typography>
                        </WithSkeleton>
                        <Typography variant="subtitle2">{stockType}</Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              </Grid>
              <Grid container size={2} justifyContent="flex-start">
                {matchedPublicPartLifecycleFormat && (
                  <Tooltip title={matchedPublicPartLifecycleFormat.tooltip}>
                    <Typography color={matchedPublicPartLifecycleFormat.color}>
                      {matchedPublicPartLifecycleFormat.label}
                    </Typography>
                  </Tooltip>
                )}
              </Grid>
            </Grid>
            <HookFormInput
              control={control}
              errors={errors}
              name={OrgPartFormConstants.orgPartNumber.id}
              label={`${org?.name ?? 'Custom'} ${
                OrgPartFormConstants.orgPartNumber.label
              }`}
              boxSx={{ mb: 0 }}
            />
          </Stack>
        )}
    </Stack>
  );
};

export default OtsPartWizard;
